import { ImprovementSuggestion } from '@lune-fe/dapi-models'
import { BannerMessage, ButtonGroup, Text } from '@lune-fe/lune-ui-lib'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { Box } from '@mui/material'
import { FC, Fragment, useState } from 'react'

import Divider from 'components/Divider'
import ImprovementsTable from 'views/DataHealth/ImprovementsTable'

import { formattedLabels, icons } from './shared'

export type ImprovementsProps = {
    mode: ImprovementSuggestion[] | null
    route: ImprovementSuggestion[] | null
    load: ImprovementSuggestion[] | null
}

enum Sections {
    MODE = 'mode',
    ROUTE = 'route',
    LOAD = 'load',
}

const HIGHLIGHT_KEYWORDS = [
    'vessel IMO',
    'flight number',
    'aircraft type',
    'vehicle and fuel type',
    'exact distance',
    'distance',
    'AIS vessel tracking',
    'both mass and TEU',
]

const regex = new RegExp(
    HIGHLIGHT_KEYWORDS.sort((a, b) => b.length - a.length) // match longer first
        .map((phrase) => phrase.replace(/[\\^$*+?.()|[\]{}]/g, '\\$&'))
        .join('|'),
    'gi',
)

const highlightDescription = (description: string) => {
    const parts = description.split(regex)
    const matches = description.match(regex)

    return (
        <>
            {parts.map((part, i) => (
                <Fragment key={i}>
                    <span>{part}</span>
                    {matches && matches[i] && <span style={{ fontWeight: 700 }}>{matches[i]}</span>}
                </Fragment>
            ))}
        </>
    )
}

const ImprovementSection: FC<{ data: ImprovementSuggestion[] | null }> = ({ data }) => {
    return data != null ? (
        <>
            {data.map((d, index) => (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 4,
                            flexDirection: 'column',
                        }}
                    >
                        <Text
                            variant={'h6'}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 3,
                            }}
                        >
                            {icons[d.label]} {formattedLabels[d.label]}
                        </Text>
                        <BannerMessage
                            sx={{
                                mb: 0,
                                backgroundColor: `Grey50`,
                                borderRadius: '16px',
                                padding: 3,
                            }}
                        >
                            <Text variant={'body3'}>
                                <LightbulbOutlinedIcon
                                    sx={{ verticalAlign: 'middle', mr: 3 / 2 }}
                                />
                                {highlightDescription(d.description)}
                            </Text>
                        </BannerMessage>
                        <ImprovementsTable data={d} />
                    </Box>
                    {index !== data.length - 1 && <Divider />}
                </>
            ))}
        </>
    ) : (
        <Box
            sx={{
                display: 'flex',
                gap: 4,
                flexDirection: 'column',
            }}
        >
            <Text variant={'h6'}>No improvements suggestions to show.</Text>
        </Box>
    )
}

const Improvements: FC<ImprovementsProps> = ({ mode, load, route }) => {
    const [selectedSection, setSelectedSection] = useState<Sections>(Sections.MODE)

    return (
        <>
            <Text variant={'h5'}>Improve my data quality</Text>
            <ButtonGroup
                value={selectedSection}
                onChange={(val) => setSelectedSection(val as Sections)}
                items={[
                    { value: Sections.MODE, label: 'Mode' },
                    { value: Sections.ROUTE, label: 'Route' },
                    { value: Sections.LOAD, label: 'Load' },
                ]}
            />
            {selectedSection === Sections.MODE && <ImprovementSection data={mode} />}
            {selectedSection === Sections.ROUTE && <ImprovementSection data={route} />}
            {selectedSection === Sections.LOAD && <ImprovementSection data={load} />}
        </>
    )
}

export default Improvements
